<template>
    <div class="forms__field_input--table" @contextmenu="onContextMenu">
        <table class="forms__field_input--table__table">
            <thead class="forms__field_input--table__table__header">
                <tr>
                    <th
                        v-for="(column, index) in modelValue?.[0] || []"
                        :key="index"
                        class="forms__field_input--table__table__header__th"
                        :class="{
                            'forms__field_input--table__context-selected':
                                index === contextMenuColumnIndex && contextMenuRowIndex === 0,
                        }"
                    >
                        <AdminText
                            v-model="modelValue[0][index]"
                            size="sm"
                            @click="activateHighlight(index, 0)"
                            @blur="closeContextMenuSelection"
                        />
                    </th>
                </tr>
            </thead>
            <tbody v-if="modelValue" class="forms__field_input--table__table__body">
                <tr v-for="(row, rowIndex) in modelValue.slice(1)" :key="rowIndex">
                    <td
                        v-for="(cell, colIndex) in row"
                        :key="colIndex"
                        class="forms__field_input--table__table__body__td"
                        :class="{
                            'forms__field_input--table__context-selected':
                                colIndex === contextMenuColumnIndex && contextMenuRowIndex === rowIndex + 1,
                        }"
                    >
                        <AdminText
                            v-model="modelValue[rowIndex + 1][colIndex]"
                            size="sm"
                            @click="activateHighlight(colIndex, rowIndex + 1)"
                            @blur="closeContextMenuSelection"
                        />
                    </td>
                </tr>
            </tbody>
        </table>

        <float-context-menu
            v-if="contextMenuShow"
            v-slot="{ close }"
            enter="transition duration-200 ease-out"
            enter-from="opacity-0 -translate-y-1"
            enter-to="opacity-100 translate-y-0"
            leave="transition duration-150 ease-in"
            leave-from="opacity-100 translate-y-0"
            leave-to="opacity-0 -translate-y-1"
        >
            <ui-popover class="w-fit">
                <ui-popover-section>
                    <ui-popover-segment>
                        <ui-popover-link :clickable="true" @click="addColumn(contextMenuColumnIndex, close)">
                            {{ lang('fields::fields.table.actions.add_column') }}
                        </ui-popover-link>
                        <ui-popover-link :clickable="true" @click="removeColumn(contextMenuColumnIndex, close)">
                            {{ lang('fields::fields.table.actions.remove_column') }}
                        </ui-popover-link>
                        <ui-popover-link :clickable="true" @click="addRow(contextMenuRowIndex, close)">
                            {{ lang('fields::fields.table.actions.add_row') }}
                        </ui-popover-link>
                        <ui-popover-link :clickable="true" @click="removeRow(contextMenuRowIndex, close)">
                            {{ lang('fields::fields.table.actions.remove_row') }}
                        </ui-popover-link>
                    </ui-popover-segment>
                </ui-popover-section>
            </ui-popover>
        </float-context-menu>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { FloatContextMenu } from '@headlessui-float/vue';
import type { FieldComponent } from '@src/Common/Components/types';

type TableData = string[][];

const defaultTableData: TableData = [
    ['Sloupec 1', 'Sloupec 2'],
    ['Řádek 1, Sloupec 1', 'Řádek 1, Sloupec 2'],
    ['Řádek 2, Sloupec 1', 'Řádek 2, Sloupec 2'],
];

// Define the modelValue with a default value
const modelValue = defineModel<TableData>({
    type: Array,
    default: () =>
        JSON.parse(
            JSON.stringify([
                ['Sloupec 1', 'Sloupec 2'],
                ['Řádek 1, Sloupec 1', 'Řádek 1, Sloupec 2'],
                ['Řádek 2, Sloupec 1', 'Řádek 2, Sloupec 2'],
            ]),
        ),
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = withDefaults(defineProps<FieldComponent>(), {});

// Ensure modelValue is initialized
if (!modelValue.value || modelValue.value.length === 0) {
    modelValue.value = [...defaultTableData];
}

const contextMenuRowIndex = ref(null);
const contextMenuColumnIndex = ref(null);
const contextMenuShow = ref(false);

const onContextMenu = (event: MouseEvent) => {
    event.preventDefault();

    const target = event.target as HTMLElement;
    const rowIndex = target.closest('tr')?.rowIndex ?? null;
    const colIndex = target.closest('td,th')?.cellIndex ?? null;

    contextMenuRowIndex.value = rowIndex;
    contextMenuColumnIndex.value = colIndex;

    contextMenuShow.value = true;
};

const closeContextMenuSelection = () => {
    setTimeout(() => {
        contextMenuRowIndex.value = null;
        contextMenuColumnIndex.value = null;
        contextMenuShow.value = false;
    }, 100);
};

const activateHighlight = (colIndex: number | null, rowIndex: number | null) => {
    contextMenuRowIndex.value = rowIndex;
    contextMenuColumnIndex.value = colIndex;
};

const addColumn = (colIndex: number | null, callback?: () => void) => {
    if (colIndex === null) return;

    modelValue.value = modelValue.value.map((row, index) => {
        return index === 0 ? [...row, `Sloupec ${row.length + 1}`] : [...row, ''];
    });

    if (typeof callback === 'function') {
        callback();
    }

    closeContextMenuSelection();
};

const removeColumn = (colIndex: number | null, callback?: () => void) => {
    if (colIndex === null) return;

    modelValue.value = modelValue.value.map((row) => row.filter((_, index) => index !== colIndex));

    if (typeof callback === 'function') {
        callback();
    }

    closeContextMenuSelection();
};

const addRow = (rowIndex: number | null, callback?: () => void) => {
    if (rowIndex === null) return;

    const newRow = modelValue.value[0].map(() => '');
    modelValue.value.splice(rowIndex + 1, 0, newRow);

    if (typeof callback === 'function') {
        callback();
    }

    closeContextMenuSelection();
};

const removeRow = (rowIndex: number | null, callback?: () => void) => {
    if (rowIndex === null) return;

    modelValue.value = modelValue.value.filter((_, index) => index !== rowIndex);

    if (typeof callback === 'function') {
        callback();
    }

    closeContextMenuSelection();
};
</script>
